export const DEFAULT_FILTERS = {
  search: "",
  status: "",
  type: "",
  level: "",
  category: "",
  shared: "",
  sort: "dateAsc",
  perPage: 25,
};

export const STATUS_OPTIONS = [
  { value: "", label: "Tous les statuts" },
  { value: "upcoming", label: "À venir" },
  { value: "ongoing", label: "En cours" },
  { value: "past", label: "Passés" },
  { value: "unknown", label: "Date inconnue" },
];

export const SHARED_OPTIONS = [
  { value: "", label: "Tous les événements" },
  { value: "shared", label: "Partagés" },
  { value: "private", label: "Non partagés" },
];

export const FIXED_EVENT_CATEGORIES = [
  "Eveils",
  "Mini-Poussins",
  "Poussins",
  "Benjamins",
  "Minimes",
  "Cadets",
  "Juniors",
  "Séniors",
  "Vétérans",
];

export function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function getCategoryKey(value) {
  const text = normalizeText(value)
    .replace(/\(e\)/g, "")
    .replace(/\s+/g, "-");

  if (text.includes("eveil")) return "eveil";
  if (text.includes("mini-poussin")) return "mini-poussin";
  if (text.includes("poussin")) return "poussin";
  if (text.includes("benjamin")) return "benjamin";
  if (text.includes("minime")) return "minime";
  if (text.includes("cadet")) return "cadet";
  if (text.includes("junior")) return "junior";
  if (text.includes("senior") || text.includes("sénior")) return "senior";
  if (text.includes("veteran") || text.includes("vétéran")) return "veteran";

  return text;
}

export function formatNumber(value) {
  return new Intl.NumberFormat("fr-FR").format(Number(value || 0));
}

export function extractEvents(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.events)) return data.events;
  if (Array.isArray(data?.Events)) return data.Events;
  if (Array.isArray(data?.data)) return data.data;

  return [];
}

export function toDate(value) {
  if (!value) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date;
}

export function getTimestamp(value) {
  const date = toDate(value);

  return date ? date.getTime() : 0;
}

export function getEventStart(event) {
  return event?.start || event?.date_event || event?.date || null;
}

export function getEventEnd(event) {
  return event?.end || event?.end_date || event?.date_fin || null;
}

export function getEventTitle(event) {
  return event?.titre || event?.title || event?.nom_event || "Événement sans titre";
}

export function getEventType(event) {
  return event?.type_event || event?.type || "Non renseigné";
}

export function getEventLevel(event) {
  return event?.level_event || event?.niveau_event || event?.level || "Non renseigné";
}

export function getEventCategory(event) {
  return event?.cate_event || event?.category_event || event?.category || "Non renseignée";
}

export function getEventLocation(event) {
  return event?.lieu_event || event?.location || event?.lieu || "Lieu non renseigné";
}

export function getEventDescription(event) {
  return (
    event?.description ||
    event?.description_event ||
    event?.content ||
    event?.details ||
    ""
  );
}

export function isEventShared(event) {
  return (
    event?.is_shared === true ||
    event?.is_shared === 1 ||
    event?.is_shared === "1" ||
    event?.is_shared === "true"
  );
}

export function formatDateFr(value) {
  const date = toDate(value);

  if (!date) return "Date non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function formatLongDateFr(value) {
  const date = toDate(value);

  if (!date) return "Date non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatTimeFr(value) {
  const date = toDate(value);

  if (!date) return "";

  return new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function formatDateTimeFr(value) {
  const date = toDate(value);

  if (!date) return "Date non renseignée";

  const dateLabel = formatDateFr(value);
  const timeLabel = formatTimeFr(value);

  return timeLabel ? `${dateLabel} · ${timeLabel}` : dateLabel;
}

export function getEventStatus(event) {
  const start = toDate(getEventStart(event));
  const end = toDate(getEventEnd(event));
  const now = new Date();

  if (!start) return "unknown";

  if (start > now) return "upcoming";

  if (end && end >= now) return "ongoing";

  return "past";
}

export function getEventStatusLabel(event) {
  const status = typeof event === "string" ? event : getEventStatus(event);

  if (status === "upcoming") return "À venir";
  if (status === "ongoing") return "En cours";
  if (status === "past") return "Passé";

  return "Date inconnue";
}

export function getEventStatusStyle(event) {
  const status = typeof event === "string" ? event : getEventStatus(event);

  if (status === "upcoming") {
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200";
  }

  if (status === "ongoing") {
    return "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200";
  }

  if (status === "past") {
    return "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-white/60";
  }

  return "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-200";
}

export function getEventGradient(event) {
  const status = getEventStatus(event);

  if (status === "upcoming") return "from-emerald-600 to-teal-700";
  if (status === "ongoing") return "from-amber-500 to-orange-600";
  if (status === "past") return "from-slate-700 to-slate-950";

  return "from-red-600 to-rose-700";
}

export function getEventTypeStyle(event) {
  const type = normalizeText(getEventType(event));

  if (type.includes("competition")) {
    return "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-200";
  }

  if (type.includes("stage")) {
    return "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-200";
  }

  if (type.includes("passage")) {
    return "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200";
  }

  if (type.includes("cours") || type.includes("entrainement")) {
    return "bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200";
  }

  if (type.includes("tournoi")) {
    return "bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-200";
  }

  return "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70";
}

export function getUniqueValues(events = [], getter) {
  const values = events
    .map(getter)
    .filter(Boolean)
    .filter((value) => value !== "Non renseigné")
    .filter((value) => value !== "Non renseignée")
    .filter((value) => value !== "Lieu non renseigné");

  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b, "fr"));
}

export function getUniqueTypes(events = []) {
  return getUniqueValues(events, getEventType);
}

export function getUniqueLevels(events = []) {
  return getUniqueValues(events, getEventLevel);
}

export function getUniqueCategories(events = []) {
  return getUniqueValues(events, getEventCategory);
}

export function filterEvents(events = [], filters = DEFAULT_FILTERS) {
  const search = normalizeText(filters.search);
  const status = String(filters.status || "");
  const type = normalizeText(filters.type);
  const level = normalizeText(filters.level);
  const category = getCategoryKey(filters.category);
  const shared = String(filters.shared || "");

  return events.filter((event) => {
    const eventStatus = getEventStatus(event);
    const eventShared = isEventShared(event);

    const haystack = normalizeText(
      [
        getEventTitle(event),
        getEventType(event),
        getEventLevel(event),
        getEventCategory(event),
        getEventLocation(event),
        getEventDescription(event),
      ].join(" "),
    );

    const matchesSearch = !search || haystack.includes(search);

    const matchesStatus = !status || eventStatus === status;

    const matchesType = !type || normalizeText(getEventType(event)) === type;

    const matchesLevel = !level || normalizeText(getEventLevel(event)) === level;

    const matchesCategory =
      !category || getCategoryKey(getEventCategory(event)) === category;

    const matchesShared =
      !shared ||
      (shared === "shared" && eventShared) ||
      (shared === "private" && !eventShared);

    return (
      matchesSearch &&
      matchesStatus &&
      matchesType &&
      matchesLevel &&
      matchesCategory &&
      matchesShared
    );
  });
}

export function sortEvents(events = [], sort = "dateAsc") {
  return [...events].sort((a, b) => {
    const titleA = normalizeText(getEventTitle(a));
    const titleB = normalizeText(getEventTitle(b));

    const startA = getTimestamp(getEventStart(a));
    const startB = getTimestamp(getEventStart(b));

    const idA = Number(a?.id || 0);
    const idB = Number(b?.id || 0);

    if (sort === "dateDesc") return startB - startA;
    if (sort === "titleAsc") return titleA.localeCompare(titleB, "fr");
    if (sort === "titleDesc") return titleB.localeCompare(titleA, "fr");
    if (sort === "typeAsc") {
      return normalizeText(getEventType(a)).localeCompare(
        normalizeText(getEventType(b)),
        "fr",
      );
    }
    if (sort === "levelAsc") {
      return normalizeText(getEventLevel(a)).localeCompare(
        normalizeText(getEventLevel(b)),
        "fr",
      );
    }
    if (sort === "recent") return idB - idA;
    if (sort === "oldest") return idA - idB;

    return startA - startB;
  });
}

export function paginateItems(items = [], page = 1, perPage = 25) {
  const safePerPage = Number(perPage || 25);
  const totalPages = Math.max(Math.ceil(items.length / safePerPage), 1);
  const safePage = Math.min(Math.max(Number(page || 1), 1), totalPages);
  const start = (safePage - 1) * safePerPage;

  return {
    page: safePage,
    totalPages,
    items: items.slice(start, start + safePerPage),
  };
}

export function buildEventStats(events = []) {
  let upcoming = 0;
  let ongoing = 0;
  let past = 0;
  let unknown = 0;
  let shared = 0;
  let missingLocation = 0;

  events.forEach((event) => {
    const status = getEventStatus(event);

    if (status === "upcoming") upcoming += 1;
    else if (status === "ongoing") ongoing += 1;
    else if (status === "past") past += 1;
    else unknown += 1;

    if (isEventShared(event)) shared += 1;

    const location = getEventLocation(event);
    if (!location || location === "Lieu non renseigné") missingLocation += 1;
  });

  return {
    total: events.length,
    upcoming,
    ongoing,
    past,
    unknown,
    shared,
    privateEvents: events.length - shared,
    missingLocation,
  };
}